import type { ReactNode } from "react";
import { Button } from "@/shared/components/button/button-untitledui";
import { LoadingIndicator } from "@/shared/components/loading-indicator";
import { cx } from "@/utils/cx";
import { TABLE_LISTING_STYLES, Table } from "./table";

/**
 * O que o corpo da tabela deve apresentar.
 * - `loading`: primeira carga, sem dados para exibir.
 * - `error`: falha sem dados para exibir.
 * - `empty`: resposta concluída sem resultados.
 * - `data`: há linhas para exibir (inclusive durante uma atualização).
 */
export type TableView = "loading" | "error" | "empty" | "data";

export type TableViewState = {
	view: TableView;
	/** Atualização em andamento com as linhas atuais preservadas. */
	isRefreshing: boolean;
	/** A última atualização falhou, mas as linhas anteriores continuam disponíveis. */
	hasRefreshError: boolean;
};

export type ResolveTableViewStateInput = {
	itemCount: number;
	isLoading?: boolean;
	isError?: boolean;
};

/**
 * Convenção de estados das tabelas de listagem.
 *
 * Com dados, a tabela nunca é trocada por carregamento ou erro: a atualização
 * marca a região como ocupada e uma falha posterior vira um aviso acima das linhas.
 * Sem dados, carregamento tem precedência sobre erro (nova tentativa em andamento)
 * e erro tem precedência sobre vazio, para que uma falha não pareça ausência de resultados.
 */
export function resolveTableViewState({
	itemCount,
	isLoading = false,
	isError = false,
}: ResolveTableViewStateInput): TableViewState {
	if (itemCount > 0) {
		return {
			view: "data",
			isRefreshing: isLoading,
			hasRefreshError: isError && !isLoading,
		};
	}

	if (isLoading) {
		return { view: "loading", isRefreshing: false, hasRefreshError: false };
	}

	if (isError) {
		return { view: "error", isRefreshing: false, hasRefreshError: false };
	}

	return { view: "empty", isRefreshing: false, hasRefreshError: false };
}

export type TableStateRowState =
	| "loading"
	| "empty"
	| "error"
	| "refresh-error";

export type TableStateRowProps = {
	state: TableStateRowState;
	/** Quantidade de colunas visíveis, incluindo a coluna de ações. */
	colSpan: number;
	/** Texto anunciado durante a primeira carga. */
	loadingLabel?: string;
	/** Conteúdo do estado vazio. Aceita texto ou um componente de vazio do domínio. */
	emptyContent?: ReactNode;
	/** Mensagem da falha sem dados. */
	errorMessage?: ReactNode;
	/**
	 * Conteúdo completo do erro sem dados, para domínios com ilustração e ação
	 * próprias. Substitui `errorMessage` e o botão de nova tentativa padrão.
	 */
	errorContent?: ReactNode;
	/** Mensagem da falha de atualização com dados preservados. */
	refreshErrorMessage?: ReactNode;
	/** Ação de nova tentativa. O botão só aparece quando informada. */
	onRetry?: () => void;
	retryLabel?: string;
};

export const TABLE_STATE_DEFAULT_MESSAGES = {
	loading: "Carregando dados",
	empty: "Nenhum resultado encontrado.",
	error: "Não foi possível carregar os dados.",
	refreshError:
		"Não foi possível atualizar os dados. Os resultados exibidos podem estar desatualizados.",
	retry: "Tentar novamente",
} as const;

const RetryButton = ({
	label,
	onRetry,
}: {
	label: string;
	onRetry: () => void;
}) => (
	<Button color="secondary" onClick={onRetry} size="sm">
		{label}
	</Button>
);

function renderStateContent({
	state,
	loadingLabel,
	emptyContent,
	errorMessage,
	errorContent,
	refreshErrorMessage,
	onRetry,
	retryLabel,
}: Required<Omit<TableStateRowProps, "colSpan" | "onRetry" | "errorContent">> &
	Pick<TableStateRowProps, "onRetry" | "errorContent">) {
	if (state === "loading") {
		return (
			<output className="flex flex-col items-center justify-center gap-2 py-6">
				<LoadingIndicator size="md" type="line-simple" />
				<span className="sr-only">{loadingLabel}</span>
			</output>
		);
	}

	if (state === "empty") {
		return (
			<div className="flex justify-center py-6 text-sm text-tertiary">
				{emptyContent}
			</div>
		);
	}

	if (state === "error") {
		if (errorContent) {
			return <div role="alert">{errorContent}</div>;
		}

		return (
			<div
				className="flex flex-col items-center justify-center gap-3 py-6 text-center"
				role="alert"
			>
				<p className="text-sm text-tertiary">{errorMessage}</p>
				{!!onRetry && <RetryButton label={retryLabel} onRetry={onRetry} />}
			</div>
		);
	}

	return (
		<div
			className="flex flex-wrap items-center justify-between gap-3 py-1"
			role="alert"
		>
			<p className="text-error-primary text-sm">{refreshErrorMessage}</p>
			{!!onRetry && <RetryButton label={retryLabel} onRetry={onRetry} />}
		</div>
	);
}

/**
 * Linha de estado dentro de `Table.Body`, ocupando todas as colunas.
 * Mantém cabeçalho e contêiner da tabela visíveis em todos os estados.
 */
export const TableStateRow = ({
	state,
	colSpan,
	loadingLabel = TABLE_STATE_DEFAULT_MESSAGES.loading,
	emptyContent = TABLE_STATE_DEFAULT_MESSAGES.empty,
	errorMessage = TABLE_STATE_DEFAULT_MESSAGES.error,
	errorContent,
	refreshErrorMessage = TABLE_STATE_DEFAULT_MESSAGES.refreshError,
	onRetry,
	retryLabel = TABLE_STATE_DEFAULT_MESSAGES.retry,
}: TableStateRowProps) => (
	<Table.Row
		className="h-auto hover:bg-transparent"
		data-state={state}
		highlightSelectedRow={false}
		id={`table-state-${state}`}
		// A coleção do React Aria não aceita trocar o id de um item montado.
		// A chave por estado monta uma linha nova quando o estado muda.
		key={state}
	>
		<Table.Cell
			className={cx("whitespace-normal", TABLE_LISTING_STYLES.stateCell)}
			colSpan={Math.max(colSpan, 1)}
		>
			{renderStateContent({
				state,
				loadingLabel,
				emptyContent,
				errorMessage,
				errorContent,
				refreshErrorMessage,
				onRetry,
				retryLabel,
			})}
		</Table.Cell>
	</Table.Row>
);
